import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Heart,
  ChevronLeft,
  Check,
  Search,
  X,
  User,
  Users,
  Lock,
  Globe
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MobileShoutOuts = ({ user }) => {
  const navigate = useNavigate();
  const [shoutOuts, setShoutOuts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  
  const [formData, setFormData] = useState({
    recipient_ids: [],
    recipient_names: [],
    is_team: false,
    team_name: "",
    what_they_did: "",
    impact: "",
    is_private: false
  });

  useEffect(() => {
    fetchShoutOuts();
    fetchUsers();
  }, []);

  const fetchShoutOuts = async () => {
    try {
      const response = await axios.get(`${API}/shout-outs`);
      setShoutOuts(response.data);
    } catch (error) {
      console.error("Failed to fetch shout outs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data.filter(u => u.status === "approved"));
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.is_team && formData.recipient_ids.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }
    
    if (formData.is_team && !formData.team_name.trim()) {
      toast.error("Please enter a team name");
      return;
    }
    
    if (!formData.what_they_did.trim()) {
      toast.error("Please describe what they did");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/shout-outs`, {
        ...formData,
        date: new Date().toISOString().split("T")[0]
      });

      toast.success("Shout out sent!");
      setDialogOpen(false);
      resetForm();
      fetchShoutOuts();
    } catch (error) {
      toast.error("Failed to send shout out");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      recipient_ids: [],
      recipient_names: [],
      is_team: false,
      team_name: "",
      what_they_did: "",
      impact: "",
      is_private: false
    });
    setUserSearch("");
  };

  const toggleRecipient = (selectedUser) => {
    const isSelected = formData.recipient_ids.includes(selectedUser.id);
    if (isSelected) {
      setFormData({
        ...formData,
        recipient_ids: formData.recipient_ids.filter(id => id !== selectedUser.id),
        recipient_names: formData.recipient_names.filter(name => name !== selectedUser.name)
      });
    } else {
      setFormData({
        ...formData,
        recipient_ids: [...formData.recipient_ids, selectedUser.id],
        recipient_names: [...formData.recipient_names, selectedUser.name]
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return "Today";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const filteredShoutOuts = shoutOuts.filter((so) => {
    if (activeTab === "given") return so.author_id === user?.id;
    if (activeTab === "received") return so.recipient_ids?.includes(user?.id);
    return true;
  });

  const filteredUsers = allUsers.filter(u => 
    u.id !== user?.id &&
    (u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
     u.email?.toLowerCase().includes(userSearch.toLowerCase()))
  );

  return (
    <div className="px-4 py-4" data-testid="mobile-shoutouts">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/mobile")}
            className="h-9 w-9"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold">Shout Outs</h1>
            <p className="text-xs text-muted-foreground">Recognition & thanks</p>
          </div>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          size="sm"
          className="bg-gradient-to-r from-pink-500 to-rose-600 shadow-md"
          data-testid="mobile-add-shoutout-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Give
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
          <TabsTrigger value="given" className="text-xs">Given</TabsTrigger>
          <TabsTrigger value="received" className="text-xs">Received</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Shout Outs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : filteredShoutOuts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Heart className="h-12 w-12 text-muted-foreground mb-3" strokeWidth={1} />
            <p className="font-medium">No shout outs</p>
            <p className="text-sm text-muted-foreground mt-1">
              Tap "Give" to thank a colleague
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredShoutOuts.map((shoutOut) => (
            <Card key={shoutOut.id} className="overflow-hidden" data-testid={`mobile-shoutout-${shoutOut.id}`}>
              <div className="h-1 bg-gradient-to-r from-pink-400 to-rose-500" />
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                    {shoutOut.is_team ? (
                      <Users className="h-5 w-5 text-pink-600" />
                    ) : (
                      <Heart className="h-5 w-5 text-pink-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">From</span>
                      <span className="font-medium text-sm">{shoutOut.author_name}</span>
                    </div>
                    <p className="font-semibold text-sm mt-0.5">
                      {shoutOut.is_team 
                        ? shoutOut.team_name 
                        : shoutOut.recipient_names?.join(", ")}
                    </p>
                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                      {shoutOut.what_they_did === "[Private]" 
                        ? <span className="italic text-muted-foreground">(Details private)</span>
                        : shoutOut.what_they_did}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">{formatDate(shoutOut.date)}</span>
                      {shoutOut.is_private && (
                        <Badge variant="outline" className="text-xs">
                          <Lock className="h-2.5 w-2.5 mr-1" /> Private
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Shout Out Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] rounded-2xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-lg font-heading flex items-center gap-2">
              <Heart className="h-5 w-5 text-pink-500" />
              Give a Shout Out
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
            {/* Team Toggle */}
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Thank a team?</Label>
                <p className="text-xs text-muted-foreground">
                  Toggle on for group recognition
                </p>
              </div>
              <Switch
                checked={formData.is_team}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  is_team: checked,
                  recipient_ids: [],
                  recipient_names: []
                })}
                data-testid="mobile-is-team-toggle"
              />
            </div>

            {/* Team Name or Recipients */}
            {formData.is_team ? (
              <div>
                <Label htmlFor="team_name" className="text-sm">Team Name *</Label>
                <Input
                  id="team_name"
                  value={formData.team_name}
                  onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                  placeholder="e.g., Children's Services Team"
                  className="mt-1.5"
                  data-testid="mobile-team-name"
                />
              </div>
            ) : (
              <div>
                <Label className="text-sm">Select Recipients *</Label>
                
                {/* Selected */}
                {formData.recipient_names.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                    {formData.recipient_names.map((name, idx) => (
                      <Badge key={idx} className="bg-pink-100 text-pink-700 pr-1">
                        {name}
                        <button
                          type="button"
                          onClick={() => {
                            const id = formData.recipient_ids[idx];
                            setFormData({
                              ...formData,
                              recipient_ids: formData.recipient_ids.filter(i => i !== id),
                              recipient_names: formData.recipient_names.filter((_, i) => i !== idx)
                            });
                          }}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                <Input
                  placeholder="Search colleagues..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="mt-1.5"
                  data-testid="mobile-user-search"
                />
                
                <div className="mt-2 max-h-32 overflow-y-auto border rounded-lg">
                  {filteredUsers.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-3">No users found</p>
                  ) : (
                    filteredUsers.slice(0, 5).map((u) => (
                      <div
                        key={u.id}
                        className={`flex items-center justify-between p-2.5 cursor-pointer transition-colors ${
                          formData.recipient_ids.includes(u.id)
                            ? "bg-pink-50"
                            : "hover:bg-stone-50"
                        }`}
                        onClick={() => toggleRecipient(u)}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{u.name}</span>
                        </div>
                        {formData.recipient_ids.includes(u.id) && (
                          <Check className="h-4 w-4 text-pink-600" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* What they did */}
            <div>
              <Label htmlFor="what_they_did" className="text-sm">What did they do? *</Label>
              <Textarea
                id="what_they_did"
                value={formData.what_they_did}
                onChange={(e) => setFormData({ ...formData, what_they_did: e.target.value })}
                placeholder="Describe their contribution..."
                rows={2}
                className="mt-1.5 resize-none"
                data-testid="mobile-what-they-did"
              />
            </div>

            {/* Impact */}
            <div>
              <Label htmlFor="impact" className="text-sm">Impact (optional)</Label>
              <Textarea
                id="impact"
                value={formData.impact}
                onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                placeholder="Why does it matter?"
                rows={2}
                className="mt-1.5 resize-none"
                data-testid="mobile-impact"
              />
            </div>

            {/* Privacy Toggle */}
            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                {formData.is_private ? (
                  <Lock className="h-4 w-4 text-stone-500" />
                ) : (
                  <Globe className="h-4 w-4 text-green-600" />
                )}
                <div>
                  <Label className="text-sm font-medium">
                    {formData.is_private ? "Private" : "Public"}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.is_private ? "Only recipients see details" : "Visible to all staff"}
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.is_private}
                onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
                data-testid="mobile-is-private-toggle"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-600"
                disabled={submitting}
                data-testid="mobile-submit-shoutout-btn"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <Heart className="h-4 w-4 mr-1" /> Send
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
